(function () {
  'use strict';

  // Classic, non-module script (same reasoning as assets/pricing-tiers.js --
  // loaded via a plain <script defer> in snippets/header-actions.liquid so
  // it runs on every page, product or cart, without a build step). The
  // event name is hardcoded rather than imported from '@theme/events' for
  // the same reason pricing-tiers.js hardcodes 'quantity-selector:update'.
  //
  // DEVIATION FROM THE ORIGINAL PLAN: dynamic `import('@shopify/events')`
  // (and `import('@theme/utilities')`) was tried first, since it resolves
  // through the theme's existing importmap (snippets/scripts.liquid) the
  // same way a `type="module"` script's static import would, in principle.
  // In practice, whatever pushes/serves this classic (non-`type="module"`)
  // asset transpiles its `import()` calls down to
  // `Promise.resolve().then(function(){return __toESM(require("@shopify/
  // events"))})` -- confirmed by fetching the deployed asset's own served
  // bytes -- and there is no `require()` in a browser, so every dynamic
  // import threw `ReferenceError: require is not defined` and the whole
  // reconciler silently no-op'd. Rather than depend on a classic script's
  // dynamic import surviving whatever transform is in play, this
  // constructs the CartLinesUpdateEvent contract by hand instead: a plain
  // bubbling Event carrying its own `.promise` (resolving to `{cart,
  // detail}`, exactly the shape assets/standard-events.d.ts documents and
  // cart-icon.js/component-cart-items.js already consume) with no import of
  // '@shopify/events' or '@theme/utilities' at all.
  var CART_LINES_UPDATE_EVENT_NAME = 'shopify:cart:lines-update';
  var RECONCILE_SOURCE = 'pricing-tiers-cart-reconcile';
  var TIER_OPTION_NAME = 'Pricing Tier';

  // Mirrors assets/pricing-tiers.js's getApplicableTier exactly -- same
  // semantics (highest min_qty tier whose min_qty <= qty) must apply here
  // too, since this is reconciling toward the same tier table.
  function getApplicableTier(qty, tiers) {
    return tiers.reduce(function (best, tier) {
      return qty >= tier.min_qty ? tier : best;
    }, tiers[0]);
  }

  function readJSON(elementId) {
    var el = document.getElementById(elementId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  // Same technique as quick-order-list.js's #getSectionIds(): every
  // cart-items-component on the page (cart drawer and/or full cart page,
  // whichever is present) needs its section re-rendered by our own
  // /cart/update.js call.
  function getSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (component) {
      var id = component.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function getTieredProductIds() {
    var ids = [];
    document.querySelectorAll('[id^="CartPricingTiersData-"]').forEach(function (el) {
      var id = el.id.slice('CartPricingTiersData-'.length);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  // The "non-tier option signature" of an ordered option-value array --
  // used both for grouping /cart.js line items and for matching a
  // candidate variant, so it must be computed identically in both places.
  function nonTierSignature(optionValues, tierIndex) {
    var filtered = optionValues.filter(function (_value, index) {
      return index !== tierIndex;
    });
    return JSON.stringify(filtered);
  }

  /**
   * Reads the live /cart.js state and, for every tiered product represented
   * in it, figures out whether its lines need consolidating onto a
   * different tier/variant. Returns a combined `updates` object (variant id
   * -> target quantity, 0 to clear) for every correction needed across all
   * products/groups, or null when nothing needs correcting.
   */
  function computeCorrections(cart) {
    var productIds = getTieredProductIds();
    if (!productIds.length) return null;

    var cartItems = (cart && cart.items) || [];
    var updates = {};
    var anyChange = false;

    productIds.forEach(function (productId) {
      var tiers = readJSON('CartPricingTiersData-' + productId);
      var productOptions = readJSON('CartPricingTiersOptions-' + productId);
      var variants = readJSON('CartPricingTiersVariants-' + productId);

      if (!Array.isArray(tiers) || !tiers.length) return;
      if (!Array.isArray(productOptions) || !productOptions.length) return;
      if (!Array.isArray(variants) || !variants.length) return;

      var tierIndex = productOptions.indexOf(TIER_OPTION_NAME);
      if (tierIndex === -1) return; // fail safe -- shouldn't happen given Step 1's gating

      // Group this product's cart lines by their non-tier option signature
      // (e.g. "same color, any tier" all collapse into one group).
      var groups = {};
      cartItems.forEach(function (item) {
        if (String(item.product_id) !== String(productId)) return;

        var optionValues = (item.options_with_values || []).map(function (option) {
          return option.value;
        });
        var signature = nonTierSignature(optionValues, tierIndex);

        if (!groups[signature]) groups[signature] = { totalQty: 0, variantQty: {} };
        groups[signature].totalQty += item.quantity;
        var variantId = String(item.variant_id);
        groups[signature].variantQty[variantId] = (groups[signature].variantQty[variantId] || 0) + item.quantity;
      });

      Object.keys(groups).forEach(function (signature) {
        var group = groups[signature];
        var totalQty = group.totalQty;
        if (!totalQty) return;

        var tier = getApplicableTier(totalQty, tiers);
        var priceKey = tier.price.toFixed(2);

        var targetVariant = variants.filter(function (variant) {
          if (variant.price !== priceKey) return false;
          return nonTierSignature(variant.options || [], tierIndex) === signature;
        })[0];

        if (!targetVariant) return; // fail safe -- no matching real variant, leave cart as-is

        var variantIds = Object.keys(group.variantQty);
        var alreadyCorrect =
          variantIds.length === 1 &&
          variantIds[0] === String(targetVariant.id) &&
          group.variantQty[variantIds[0]] === totalQty;

        if (alreadyCorrect) return;

        variantIds.forEach(function (variantId) {
          if (variantId !== String(targetVariant.id)) {
            updates[variantId] = 0;
          }
        });
        updates[String(targetVariant.id)] = totalQty;
        anyChange = true;
      });
    });

    return anyChange ? updates : null;
  }

  /**
   * A deferred promise -- the same shape CartLinesUpdateEvent.createPromise()
   * returns in @shopify/events, replicated by hand (see the DEVIATION note
   * above for why this file doesn't import that module).
   */
  function createDeferredPromise() {
    var resolve, reject;
    var promise = new Promise(function (res, rej) {
      resolve = res;
      reject = rej;
    });
    return { promise: promise, resolve: resolve, reject: reject };
  }

  /**
   * Builds the `CartSummary` shape assets/standard-events.d.ts documents
   * (id, totalQuantity, cost, lines[], discountCodes[]) from a raw /cart/
   * update.js AJAX response -- the same conversion @shopify/events'
   * `createCartFromAjaxResponse` performs, replicated by hand for the same
   * reason as createDeferredPromise above. Only `totalQuantity` is actually
   * read by any listener in this theme today (cart-icon.js), but the rest
   * is filled in for parity with the real contract.
   */
  function buildCartSummaryFromAjax(ajaxCart) {
    if (!ajaxCart) return null;
    var currency = ajaxCart.currency || '';
    return {
      id: ajaxCart.token,
      totalQuantity: ajaxCart.item_count,
      cost: {
        totalAmount: {
          amount: (Number(ajaxCart.total_price) / 100).toFixed(2),
          currencyCode: currency,
        },
      },
      lines: (ajaxCart.items || []).map(function (item) {
        return {
          id: item.key,
          quantity: item.quantity,
          cost: {
            totalAmount: {
              amount: (Number(item.final_line_price) / 100).toFixed(2),
              currencyCode: currency,
            },
          },
        };
      }),
      discountCodes: (ajaxCart.discount_codes || []).map(function (discount) {
        return { applicable: !!discount.applicable, code: discount.code };
      }),
    };
  }

  /**
   * Dispatches a plain Event replicating CartLinesUpdateEvent's own-property
   * shape (action/context/lines/promise/detail set directly on the event,
   * per assets/standard-events.d.ts -- NOT nested under a native
   * CustomEvent `.detail`). `bubbles: true` matches the real event so any
   * listener attached to a specific element rather than `document` still
   * sees it.
   */
  function dispatchCartLinesUpdateEvent(payload) {
    var event = new Event(CART_LINES_UPDATE_EVENT_NAME, { bubbles: true, cancelable: false });
    event.action = payload.action;
    event.context = payload.context;
    event.lines = payload.lines;
    event.promise = payload.promise;
    if (payload.detail !== undefined) event.detail = payload.detail;
    document.dispatchEvent(event);
    return event;
  }

  /**
   * POSTs the combined `updates` to /cart/update.js, broadcasting the same
   * shopify:cart:lines-update contract other components already rely on
   * (cart-icon.js, component-cart-items.js) so they re-render themselves
   * from the response -- no DOM-patching code needed here.
   */
  function applyCorrections(updates) {
    var deferred = createDeferredPromise();

    dispatchCartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: Object.keys(updates).map(function (variantId) {
        return { id: variantId, quantity: updates[variantId] };
      }),
      promise: deferred.promise,
    });

    var body = JSON.stringify({
      updates: updates,
      sections: getSectionIds().join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body,
    })
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        var data = JSON.parse(responseText);

        if (data.errors) {
          var errorMessage = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
          deferred.reject(new Error(errorMessage));
          console.error('[pricing-tiers-cart-reconcile] /cart/update.js returned an error:', errorMessage);
          return;
        }

        deferred.resolve({
          cart: buildCartSummaryFromAjax(data),
          detail: {
            sections: data.sections,
            items: data.items,
            itemCount: data.item_count,
            source: RECONCILE_SOURCE,
            didError: false,
          },
        });
      })
      .catch(function (error) {
        deferred.reject(error);
        console.error('[pricing-tiers-cart-reconcile] /cart/update.js request failed:', error);
      });

    // This function's own job is done once the request is in flight --
    // callers only need to know the correction was attempted, not wait for
    // cart-icon.js/component-cart-items.js to finish re-rendering.
    return deferred.promise.catch(function () {
      // Already logged above; swallow here so a rejected correction never
      // surfaces as an unhandled rejection from reconcile().
    });
  }

  /**
   * Best-effort background correction pass: fetches the live cart, computes
   * whatever corrections are needed, and applies them. Never throws --
   * failures are logged, not surfaced to the customer, since this is a
   * correctness safety net, not a user-facing gate.
   */
  function reconcile() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var updates = computeCorrections(cart);
        if (!updates || !Object.keys(updates).length) return;
        return applyCorrections(updates);
      })
      .catch(function (error) {
        console.error('[pricing-tiers-cart-reconcile] Reconciliation pass failed:', error);
      });
  }

  // Serializes reconcile passes so a burst of rapid mutations (e.g. several
  // quantity-selector stepper clicks) never fires overlapping
  // /cart/update.js corrections against each other.
  var reconcileChain = Promise.resolve();
  function scheduleReconcile() {
    reconcileChain = reconcileChain.then(reconcile, reconcile);
    return reconcileChain;
  }

  /**
   * @param {CustomEvent} event
   */
  function handleCartLinesUpdate(event) {
    if (!event || !event.promise) return;

    event.promise
      .then(function (result) {
        var detail = result && result.detail;
        // Guard against reconciling our own dispatch -- bounds this to at
        // most one corrective round per genuine external mutation.
        if (detail && detail.source === RECONCILE_SOURCE) return;
        return scheduleReconcile();
      })
      .catch(function (error) {
        // The mutation itself failed (e.g. network error, cart error) --
        // nothing for us to reconcile against a cart state that never
        // changed. Not our failure to report.
        if (error && error.name !== 'AbortError') {
          console.warn('[pricing-tiers-cart-reconcile] Observed cart update promise rejected:', error);
        }
      });
  }

  function boot() {
    // The listener itself is attached unconditionally, cheaply -- it does
    // no network work on its own. It MUST persist for the whole page
    // lifetime even when no CartPricingTiersData-* payload exists yet at
    // load time: a cart that starts EMPTY when this page loads (the common
    // case) has no such payload, but gains one the moment the customer's
    // very first add-to-cart on this page succeeds and morphSection
    // re-renders the cart drawer markup with it. Gating the LISTENER
    // itself behind an at-boot payload check (rather than just gating the
    // one-off correction pass below) would permanently disable
    // reconciliation for that entire page session -- silently breaking the
    // "add 1,000, then separately add 1,000 more" scenario for every
    // empty-cart visit, i.e. almost always. Every other lookup in this file
    // (getTieredProductIds, etc.) already re-reads the DOM live on each
    // call, so deferring the "is there anything to do" check to reconcile()
    // itself is what actually keeps this free on a page with no tiered
    // products, not bailing out here.
    document.addEventListener(CART_LINES_UPDATE_EVENT_NAME, handleCartLinesUpdate);

    // The one-off boot-time correction pass stays gated behind an
    // already-present payload, though: its only job is fixing pre-existing
    // bad state, which by definition requires the tiered product to already
    // be in the cart (and therefore already server-rendered) when this page
    // loaded. Fixes any inconsistency left over from before this fix
    // existed (very likely present from manual testing), not just new
    // mutations going forward.
    if (document.querySelector('[id^="CartPricingTiersData-"]')) {
      scheduleReconcile();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
